/**
 * Money, and the month it was money in (SPEC-002).
 *
 * Split out of `carProperties.js` rather than added to it, and not only because
 * that file reached its length. Everything here knows about currencies, rates
 * and calendars and nothing whatever about cars — hand it `{ amount, currency }`,
 * a rate or a `YYYY-MM` string and it is done — whereas everything left in
 * `carProperties` knows where a figure lives on a record. That is a real seam,
 * and the price rows are the only thing that crosses it.
 *
 * Same rule as the rest of the domain: `Intl` is allowed here and `t()` is not.
 * Where a symbol goes, which symbol it is, how digits group and how a month is
 * abbreviated are facts about a language, and the standard library knows all
 * four. Copy is somebody's to translate; these are not.
 */

/**
 * A price, in the currency the catalogue recorded it in.
 *
 * No fraction digits. Every price in the catalogue is a published list price or
 * an average of recorded sales, both in whole dollars, and `$3,047,885.00` on a
 * spec sheet claims a precision to the cent that an average does not have.
 *
 * `null` rather than a throw for a price it cannot draw, and the missing
 * CURRENCY is the case that matters: `Intl.NumberFormat` in currency mode with
 * no currency raises a TypeError, and a TypeError inside a render is a blank
 * screen where the honest answer was a blank cell. Null rather than the em dash
 * for the same reason `formatStampedMonth` answers null — how an absent figure
 * is DRAWN is the spec sheet's decision, and this module does not know it is
 * being drawn on one.
 */
export function formatPrice(price, { languageCode = 'en' } = {}) {
    const { amount, currency } = price || {};

    if (typeof amount !== 'number' || !Number.isFinite(amount) || typeof currency !== 'string') {
        return null;
    }

    return new Intl.NumberFormat(languageCode, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
}

/**
 * The same price with the zeroes taken off, for a gridline.
 *
 * A value axis runs to seven figures and its labels sit in a 34-unit margin at
 * nine pixels; `$3,047,885` does not go there, and truncating it in CSS would
 * put an ellipsis where a number the reader is supposed to measure against
 * should be. `notation: 'compact'` is the standard library's answer and it is
 * language-aware in the way a hand-rolled "3M" would not be — German gets
 * `3 Mio. $` off this same call, symbol, separator and abbreviation together.
 *
 * Deliberately no fraction control: the defaults give `$3M`, `$250K`, `$500`,
 * which is what an axis wants. Pinning `maximumFractionDigits` to 1 turns every
 * round tick into `$50.0K`, and a gridline with a decimal on it reads as a
 * measurement rather than as a round number somebody chose.
 *
 * For a figure a reader is meant to READ rather than measure against — the two
 * sourced prices, anywhere in a sentence — `formatPrice` is the one to call. A
 * car that is worth `$3M` is a car nobody quoted a price for.
 */
export function formatCompactPrice(price, { languageCode = 'en' } = {}) {
    const { amount, currency } = price || {};

    if (typeof amount !== 'number' || !Number.isFinite(amount) || typeof currency !== 'string') {
        return null;
    }

    return new Intl.NumberFormat(languageCode, {
        style: 'currency',
        currency,
        notation: 'compact',
    }).format(amount);
}

/**
 * A growth rate, with its sign shown even when it is positive.
 *
 * Here rather than beside the other figure formatters because it exists for one
 * caller — the value band's rate after inflation — and that rate is meaningless
 * without its direction. `+2.5%` and `2.5%` read the same in isolation, but this
 * number sits next to `−2.6%` on the next card in the deck, and a reader
 * swiping between them has to see which way each one went without reading the
 * sentence around it. `signDisplay: 'exceptZero'` is what gives the plus, and
 * each language gets the sign it actually writes: English gets a hyphen-minus,
 * Swedish the true minus at U+2212. Composing the sign here would have hard-
 * coded one of those two for everybody.
 *
 * One decimal, fixed: the bands this qualifies are a fifth of a point wide at
 * their narrowest, and a rate rounded to whole percent would put two cars in
 * visibly different bands under the same printed number.
 */
export function formatSignedPercent(rate, { languageCode = 'en' } = {}) {
    if (typeof rate !== 'number' || !Number.isFinite(rate)) {
        return null;
    }

    return new Intl.NumberFormat(languageCode, {
        style: 'percent',
        signDisplay: 'exceptZero',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    }).format(rate);
}

/**
 * The month a figure was read, as a reader would say it.
 *
 * The catalogue stamps `2026-08` because that sorts, compares and diffs; a
 * reader wants "Aug 2026", and in another language wants it in another order
 * with another abbreviation.
 *
 * Built at UTC and formatted at UTC, which is not fussiness: constructing the
 * first of the month in local time and formatting it anywhere west of Greenwich
 * lands on the last day of the PREVIOUS month, so a figure stamped `2026-08`
 * would be dated July for every reader in the Americas and nobody in London
 * would ever see it.
 *
 * @returns `null` rather than an em dash for a stamp it cannot read, because the
 *          caller's question is "is there a date to put in this label", not
 *          "what do I print".
 */
export function formatStampedMonth(stamp, { languageCode = 'en' } = {}) {
    const match = /^(\d{4})-(\d{2})$/.exec(typeof stamp === 'string' ? stamp : '');

    if (!match) {
        return null;
    }

    const [, year, month] = match;
    const monthIndex = Number(month) - 1;

    if (monthIndex < 0 || monthIndex > 11) {
        return null;
    }

    return new Intl.DateTimeFormat(languageCode, {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(new Date(Date.UTC(Number(year), monthIndex, 1)));
}
